// src/components/HRComponents/EmailWorkflows.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from "react-toastify";
import { 
  Plus, Edit2, Trash2, 
  Play, GitFork, Eye, EyeOff, Copy 
} from "lucide-react";

import api from "../../common/apiClient"; // Global apiClient [1]
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from '../../customStyles/SelectDropdown'; // Reusable custom dropdown [1]


const emptyForm = {
  name: "",
  description: "",
  subject: "",
  body: "",
  departmentId: "",
  divisionId: "",
  meeting: {
    title: "",
    date: "",
    time: "",
    location: "",
  },
  onlyActive: true,
};

const EmailWorkflows = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [departments, setDepartments] = useState([]); // Loaded dynamically based on chosen divisionId [1]
  const [divisions, setDivisions] = useState([]);
  
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [executions, setExecutions] = useState({});
  const [editingId, setEditingId] = useState(null);

  // Fetch active divisions on component mount [1]
  const fetchDivisions = useCallback(async () => {
    try {
      const res = await api.get(SummaryApi.getAllDivisions?.url || "/api/hr/divisions");
      if (res.data.success) setDivisions(res.data.data || []);
    } catch (err) {
      console.error("Failed to load divisions:", err);
    }
  }, []);

  // Fetch departments dynamically based on chosen divisionId [1]
  const handleDivisionChange = async (divisionId) => {
    setForm((f) => ({ ...f, divisionId, departmentId: "" })); // Reset division & department states [1]
    setDepartments([]);

    if (!divisionId) return;

    try {
      const res = await api.get(SummaryApi.getAllDepartments.url, { params: { divisionId } });
      if (res.data.success) {
        setDepartments(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to load departments:", err);
    }
  };

  // Load all workflows and initial dropdown options [1]
  const loadAll = async () => {
    try {
      setLoading(true);
      
      // Inline API fetching utilizing global apiClient directly [1]
      const [wfRes] = await Promise.all([
        api.get(SummaryApi.listWorkflows.url), // Matches your backend workflows route
      ]);
      setWorkflows(wfRes.data.data || wfRes.data || []);
      await fetchDivisions();
    } catch (err) {
      console.error(err);
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleMeetingChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      meeting: {
        ...f.meeting,
        [name]: value,
      },
    }));
  };

  // Handles creating or updating the unified workflow [1]
  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.subject || !form.body) {
      toast.error("Name, subject and body are required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // PUT request inline using the core apiClient [1]
        await api.put(SummaryApi.updateWorkflow.url(editingId), form);
        toast.success("Workflow updated");
      } else {
        // POST request inline using the core apiClient [1]
        await api.post(SummaryApi.createWorkflow.url, form);
        toast.success("Workflow created");
      }

      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      loadAll();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to save workflow"
      );
    } finally {
      setSaving(false);
    }
  };

  // Populate form for editing dynamically
  const handleEdit = async (wf) => {
    const emailNode = wf.nodes.find((n) => n.type === "email");
    const mongoNode = wf.nodes.find((n) => n.type === "mongoUsers");
    const divId = mongoNode?.data?.config?.divisionId || "";

    setEditingId(wf._id);

    // If division exists, load its corresponding departments dynamically first [1]
    if (divId) {
      try {
        const res = await api.get(SummaryApi.getAllDepartments.url, { params: { divisionId: divId } });
        if (res.data.success) setDepartments(res.data.data || []);
      } catch {
        setDepartments([]);
      }
    }

    setForm({
      name: wf.name || "",
      description: wf.description || "",
      subject: emailNode?.data?.config?.subject || "",
      body: emailNode?.data?.config?.body || "",
      divisionId: divId,
      departmentId: mongoNode?.data?.config?.departmentId || "",
      onlyActive: mongoNode?.data?.config?.onlyActive ?? true,
      meeting: emailNode?.data?.config?.meeting || {
        title: "",
        date: "",
        time: "",
        location: "",
      },
    });

    setShowForm(true);
  };

  const handleToggleActive = async (wf) => {
    try {
      await api.put(SummaryApi.updateWorkflow.url(wf._id), { isActive: !wf.isActive });
      toast.success(`Workflow ${wf.isActive ? "deactivated" : "activated"}`);
      loadAll();
    } catch (err) {
      toast.error("Failed to update workflow");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this workflow?")) return;
    try {
      await api.delete(SummaryApi.deleteWorkflow.url(id));
      toast.success("Workflow deleted");
      loadAll();
    } catch (err) {
      toast.error("Failed to delete workflow");
    }
  };

  // Triggers manual test run of the workflow [1]
  const handleTestRun = async (id) => {
    try {
      setRunningId(id);
      const res = await api.post(SummaryApi.testRunWorkflow.url(id), {});
      if (res.data.success || res.success) {
        toast.success(
          `Sent to ${res.data?.execution?.steps?.find((s) => s.nodeType === "email")?.output?.length || 0} user(s)`
        );
      } else {
        toast.error(res.data?.execution?.error || "Workflow run failed");
      }
      loadExecutions(id);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Test run failed");
    } finally {
      setRunningId(null);
    }
  };

  // Fetch paginated or full execution logs for a workflow [1]
  const loadExecutions = async (id) => {
    try {
      const res = await api.get(SummaryApi.listWorkflowExecutions.url(id));
      setExecutions((prev) => ({ ...prev, [id]: res.data.data || res.data || [] }));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!executions[id]) loadExecutions(id);
  };

  const copyWebhookUrl = (webhookId) => {
    const url = `${SummaryApi}/api/webhook/${webhookId}`;
    navigator.clipboard.writeText(url);
    toast.success("Webhook URL copied");
  };

  if (loading) {
    return <div className="p-5 text-center text-gray-400">Loading Workflows...</div>;
  }

  return (
    <div className="p-4">
      {/* Header section with back navigation and create trigger button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-1.5 sm:p-2 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Email Workflows</h1>
            <p className="text-xs text-gray-500 mt-1">
              Fixed pipeline: Webhook Trigger → Mongo Users → SMTP Email
            </p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm((s) => !s); if (showForm) setForm(emptyForm); }}
          className="bg-primary hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
        >
          {showForm ? "Cancel" : <><Plus className="h-4 w-4" /> New Workflow</>}
        </button>
      </div>

      {/* Styled Creation/Edition Form */}
      {showForm && (
        <form
          onSubmit={handleCreateSubmit}
          className="bg-white rounded-xl border p-6 space-y-5 mb-8 shadow-sm animate-in fade-in duration-300"
        >
          <h2 className="text-sm font-semibold text-gray-800 pb-2 border-b">
            {editingId ? "Update Workflow Details" : "Create New Fixed Pipeline Workflow"}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workflow Name *
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
                placeholder="e.g. New Policy Announcement"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                name="description"
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
                placeholder="Describe the workflow's objective (optional)"
              />
            </div>

            {/* Custom SelectDropdown for Divisions [1] */}
            <SelectDropdown
              label="Division (optional)"
              options={[{ name: "All Divisions", _id: "" }, ...divisions]}
              value={form.divisionId}
              onChange={handleDivisionChange}
              valueKey="_id"
              labelKey="name"
              searchable
            />

            {/* Custom SelectDropdown for Departments (Disabled until Division is selected) [1] */}
            <SelectDropdown
              label="Department (optional)"
              options={[{ name: "All Departments", _id: "" }, ...departments]}
              value={form.departmentId}
              onChange={(v) => updateField('departmentId', v)}
              valueKey="_id"
              labelKey="name"
              searchable
              disabled={!form.divisionId}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Title
              </label>
              <input
                type="text"
                name="title"
                value={form.meeting.title}
                onChange={handleMeetingChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
                placeholder="e.g., Sprint Planning"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Location
              </label>
              <input
                type="text"
                name="location"
                value={form.meeting.location}
                onChange={handleMeetingChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
                placeholder="e.g., Conference Room A"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Date
              </label>
              <input
                type="date"
                name="date"
                value={form.meeting.date}
                onChange={handleMeetingChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Time
              </label>
              <input
                type="time"
                name="time"
                value={form.meeting.time}
                onChange={handleMeetingChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Email Subject *</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => updateField('subject', e.target.value)}
              placeholder="e.g. Hi {{firstName}}, important update!"
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Email Body (HTML) *</label>
            <textarea
              rows={6}
              value={form.body}
              onChange={(e) => updateField('body', e.target.value)}
              placeholder="<p>Hi {{firstName}},</p><p>We have assigned...</p>"
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm font-mono"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Available dynamic placeholders: {"{{firstName}}"}, {"{{lastName}}"}, {"{{name}}"}, {"{{email}}"}
            </p>
          </div>

          <div className="flex items-center gap-6 border-t pt-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input 
                type="checkbox" 
                name="onlyActive" 
                checked={form.onlyActive} 
                onChange={(e) => updateField('onlyActive', e.target.checked)} 
              />
              Only send to active employees
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={() => { setShowForm(false); setForm(emptyForm); setEditingId(null); }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50"
            >
              {saving ? "Saving..." : editingId ? "Save Changes" : "Create Pipeline"}
            </button>
          </div>
        </form>
      )}

      {/* Styled Workflows Card List */}
      <div className="space-y-4">
        {workflows.length === 0 && (
          <div className="text-sm text-gray-400 border rounded-xl p-8 text-center bg-white shadow-sm">
            <GitFork className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-900">No pipelines configured yet</h3>
            <p className="text-xs text-gray-500 mt-1">Click "+ New Workflow" on the top right to build your first automation.</p>
          </div>
        )}

        {workflows.map((wf) => (
          <div key={wf._id} className="bg-white border rounded-xl p-5 shadow-sm space-y-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base">{wf.name}</h3>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${
                      wf.isActive 
                        ? "bg-green-50 text-green-700 border-green-200" 
                        : "bg-gray-50 text-gray-500 border-gray-200"
                    }`}
                  >
                    {wf.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                {wf.description && <p className="text-xs text-gray-500 mt-1">{wf.description}</p>}
                
                {/* Webhook Endpoint copy section */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Webhook Endpoint:</span>
                  <code className="bg-gray-50 border border-gray-100 px-2 py-1 rounded text-xs text-red-600 font-semibold truncate max-w-xs sm:max-w-md">
                    POST {SummaryApi}/api/webhook/{wf.webhookId}
                  </code>
                  <button
                    onClick={() => copyWebhookUrl(wf.webhookId)}
                    className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
              </div>

              {/* Individual Workflow Action Buttons Panel */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => handleTestRun(wf._id)}
                  disabled={runningId === wf._id}
                  className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                >
                  <Play className="h-3.5 w-3.5" />
                  {runningId === wf._id ? "Running..." : "Test Run"}
                </button>
                <button
                  onClick={() => handleToggleActive(wf)}
                  className="bg-white border hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium"
                >
                  {wf.isActive ? "Deactivate" : "Activate"}
                </button>
                <button
                  onClick={() => toggleExpand(wf._id)}
                  className="bg-white border hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1"
                >
                  {expandedId === wf._id ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  {expandedId === wf._id ? "Hide Runs" : "View Runs"}
                </button>
                <button
                  onClick={() => handleEdit(wf)}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1"
                >
                  <Edit2 className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(wf._id)}
                  className="text-red-600 border border-red-100 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>

            {/* Collapsible Recent Execution Logs */}
            {expandedId === wf._id && (
              <div className="mt-4 border-t pt-4 animate-in fade-in slide-in-from-top-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Recent Executions Log</h4>
                {(executions[wf._id] || []).length === 0 && (
                  <p className="text-xs text-gray-400">No execution logs found yet.</p>
                )}
                <ul className="space-y-2">
                  {(executions[wf._id] || []).map((ex) => (
                    <li key={ex._id} className="text-xs flex items-center gap-2 flex-wrap">
                      <span
                        className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${
                          ex.status === "success"
                            ? "bg-green-50 text-green-700 border-green-100"
                            : ex.status === "failed"
                            ? "bg-red-50 text-red-700 border-red-100"
                            : "bg-yellow-50 text-yellow-700 border-yellow-100"
                        }`}
                      >
                        {ex.status}
                      </span>
                      <span className="text-gray-500 font-medium">
                        {new Date(ex.createdAt).toLocaleString()} · {ex.durationMs || 0}ms
                      </span>
                      {ex.error && <span className="text-red-500 font-semibold">— {ex.error}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmailWorkflows;